// listAll / fetchAllPages — read EVERY item of a list route (PFS-1549).
// The TypeScript mirror of pfs-framework pkg/storage/list.All: same walk, same
// stop conditions, same REST cap (LIST_MAX_LIMIT = list.RESTQuery maximum).
//
// For lookups that need the whole list (a map of feedback per appointment, a
// select of all specialties). Views a user pages through use ListController.
//
// The route answers list.Page (Items/Total/Limit/Offset, pfs-framework
// pkg/storage/list) and caps `limit` at the REST maximum. One oversized page
// is a 422 (limit=200 against 100, PFS-1549); one page without paging drops
// every row past it without a word (PFS-1553). Hence:
//   - the page size is the canon maximum LIST_MAX_LIMIT, never per caller;
//   - the offset advances by what each page CARRIED, so a server that answers
//     fewer than asked is followed, not skipped;
//   - it stops on an empty page or once Total is reached;
//   - a page without a numeric Total is an error, never a silent "that was all";
//   - more than MAX_PAGES pages is an error, never a silent cut.
// Lifted from sparkden-badminton's collectPages (second consumer: mednet-scheduling);
// on top of list.All: a page cap (MAX_PAGES) that fails loudly.

import { apiFetch } from "../store/api-fetch.js";
import { LIST_MAX_LIMIT } from "./list-controller.js";

/** One page of a list route in the list canon (pfs-framework list.Page). */
export interface RestListPage<T> {
  Items: T[];
  Total: number;
  Limit?: number;
  Offset?: number;
}

/** Upper bound of pages one read may take (MAX_PAGES × LIST_MAX_LIMIT rows). */
export const MAX_PAGES = 100;

/**
 * Reads every item through `fetchPage(limit, offset)`, page after page until
 * Total is reached or a page comes back empty.
 */
export async function listAll<T>(
  fetchPage: (limit: number, offset: number) => Promise<RestListPage<T>>,
): Promise<T[]> {
  const items: T[] = [];
  for (let pages = 0; ; pages++) {
    if (pages >= MAX_PAGES) {
      throw new Error(`listAll: more than ${MAX_PAGES} pages (${items.length} rows read) — refusing to cut silently`);
    }
    const page = await fetchPage(LIST_MAX_LIMIT, items.length);
    if (typeof page?.Total !== "number" || !Array.isArray(page.Items)) {
      throw new Error("listAll: the answer is not a list.Page (Items/Total)");
    }
    if (page.Total < 0) {
      throw new Error("listAll: the page reports a negative Total"); // list.ErrNegativeTotal
    }
    items.push(...page.Items);
    if (page.Items.length === 0 || items.length >= page.Total) return items;
  }
}

/**
 * Reads every item of the list route `url` (a bare path; apiFetch prepends the
 * apiBase like every other call). Query parameters already in `url` stay.
 */
export function fetchAllPages<T>(url: string): Promise<T[]> {
  const sep = url.includes("?") ? "&" : "?";
  return listAll<T>((limit, offset) =>
    apiFetch<RestListPage<T>>(`${url}${sep}limit=${limit}&offset=${offset}`),
  );
}
